/**
 * Contact Messages Page - View and manage contact form submissions
 *
 * Tammy can see all messages, reply from the portal (emails customer + marks replied),
 * or use her own email app. She also receives an email when a new message is submitted.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  HiMail,
  HiPhone,
  HiUser,
  HiRefresh,
  HiTrash,
  HiCheck,
  HiChevronDown,
  HiChevronUp,
  HiInbox,
  HiCurrencyDollar,
  HiPaperAirplane,
} from 'react-icons/hi';
import CreateQuotePayLinkModal from '../components/CreateQuotePayLinkModal';
import { adminFetch, adminHeaders } from '../lib/adminApi';

const API_URL = import.meta.env.VITE_API_URL || '';

interface ContactReply {
  id: string;
  body: string;
  sent_via: string;
  created_at: string;
}

interface Submission {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  subject: string;
  message: string;
  status: 'new' | 'read' | 'replied' | 'spam';
  admin_notes?: string | null;
  created_at: string;
  updated_at: string;
  replies?: ContactReply[];
}

interface Stats {
  total: number;
  new: number;
  read: number;
  replied: number;
  spam: number;
}

function formatDate(s: string): string {
  return new Date(s).toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function Messages() {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [stats, setStats] = useState<Stats>({ total: 0, new: 0, read: 0, replied: 0, spam: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'read' | 'replied' | 'spam'>('new');
  const [subjectContains, setSubjectContains] = useState('');
  const [subjectApply, setSubjectApply] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [quoteFor, setQuoteFor] = useState<Submission | null>(null);
  const [notesDraftById, setNotesDraftById] = useState<Record<string, string>>({});
  const [replyDraftById, setReplyDraftById] = useState<Record<string, string>>({});
  const [sendingReplyId, setSendingReplyId] = useState<string | null>(null);
  const [detailLoadingId, setDetailLoadingId] = useState<string | null>(null);
  /** Keep instructions out of Tammy's way; open only when she needs a reminder. */
  const [helpOpen, setHelpOpen] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (subjectApply.trim()) params.set('subject', subjectApply.trim());
      const res = await adminFetch(`${API_URL}/api/contact?${params}`);
      if (!res.ok) throw new Error('Failed to fetch messages');
      const data = await res.json();
      setSubmissions(data.submissions || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages');
    }
  }, [statusFilter, subjectApply]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await adminFetch(`${API_URL}/api/contact/stats`);
      if (!res.ok) return;
      const data = await res.json();
      setStats({
        total: data.total ?? 0,
        new: data.new ?? 0,
        read: data.read ?? 0,
        replied: data.replied ?? 0,
        spam: data.spam ?? 0,
      });
    } catch {
      // Non-fatal
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([fetchSubmissions(), fetchStats()]).finally(() => setLoading(false));
  }, [fetchSubmissions, fetchStats]);

  const updateStatus = async (
    id: string,
    status: 'new' | 'read' | 'replied' | 'spam',
    opts?: { silent?: boolean }
  ) => {
    setUpdatingId(id);
    try {
      const res = await adminFetch(`${API_URL}/api/contact/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Failed to update');
      const updated = await res.json();
      setSubmissions((prev) =>
        statusFilter !== 'all' && statusFilter !== status
          ? prev.filter((s) => s.id !== id)
          : prev.map((s) => (s.id === id ? updated : s))
      );
      fetchStats();
    } catch (err) {
      if (!opts?.silent) {
        alert(err instanceof Error ? err.message : 'Failed to update status');
      }
    } finally {
      setUpdatingId(null);
    }
  };

  const fetchSubmissionDetail = useCallback(async (id: string) => {
    setDetailLoadingId(id);
    try {
      const res = await adminFetch(`${API_URL}/api/contact/${id}`);
      if (!res.ok) throw new Error('Failed to load message details');
      const data = (await res.json()) as Submission;
      setSubmissions((prev) => prev.map((s) => (s.id === id ? { ...s, ...data } : s)));
      return data;
    } catch (err) {
      console.error(err);
      return null;
    } finally {
      setDetailLoadingId(null);
    }
  }, []);

  const sendPortalReply = async (s: Submission) => {
    const message = (replyDraftById[s.id] ?? '').trim();
    if (message.length < 2) {
      alert('Please type a reply (at least 2 characters).');
      return;
    }

    setSendingReplyId(s.id);
    try {
      const res = await adminFetch(`${API_URL}/api/contact/${s.id}/reply`, {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ message }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to send reply');
      }
      setSubmissions((prev) =>
        prev.map((row) =>
          row.id === s.id
            ? {
                ...row,
                ...(data.submission as Submission),
                replies: (data.replies as ContactReply[]) || row.replies,
              }
            : row
        )
      );
      setReplyDraftById((d) => ({ ...d, [s.id]: '' }));
      fetchStats();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to send reply');
    } finally {
      setSendingReplyId(null);
    }
  };

  const toggleExpanded = async (s: Submission) => {
    const isExpanded = expandedId === s.id;
    if (!isExpanded) {
      setNotesDraftById((d) => ({
        ...d,
        [s.id]: s.admin_notes ?? '',
      }));
      setReplyDraftById((d) => ({
        ...d,
        [s.id]: d[s.id] ?? '',
      }));
      if (s.status === 'new') {
        void updateStatus(s.id, 'read', { silent: true });
      }
      setExpandedId(s.id);
      void fetchSubmissionDetail(s.id);
      return;
    }
    setExpandedId(null);
  };

  const bulkUpdateStatus = async (status: 'read' | 'replied') => {
    setBulkUpdating(true);
    try {
      const res = await adminFetch(`${API_URL}/api/contact/bulk-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Failed to update');
      await Promise.all([fetchSubmissions(), fetchStats()]);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setBulkUpdating(false);
    }
  };

  const saveAdminNotes = async (s: Submission) => {
    const notes = notesDraftById[s.id] ?? s.admin_notes ?? '';
    setUpdatingId(s.id);
    try {
      const res = await adminFetch(`${API_URL}/api/contact/${s.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: s.status, admin_notes: notes }),
      });
      if (!res.ok) throw new Error('Failed to save notes');
      const updated = await res.json();
      setSubmissions((prev) => prev.map((x) => (x.id === s.id ? updated : x)));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save notes');
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteSubmission = async (id: string) => {
    if (!confirm('Delete this message? This cannot be undone.')) return;
    try {
      const res = await adminFetch(`${API_URL}/api/contact/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete');
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
      setExpandedId((prev) => (prev === id ? null : prev));
      fetchStats();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const filtered = submissions;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Contact messages</h1>
        <p className="text-gray-500 mt-1 text-sm">
          Messages from your website contact form. You also receive an email when someone submits.
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-950">
        <button
          type="button"
          onClick={() => setHelpOpen((open) => !open)}
          aria-expanded={helpOpen}
          className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left font-medium hover:bg-amber-100 rounded-xl"
        >
          <span>✉️ How replies and pay links work</span>
          {helpOpen ? (
            <HiChevronUp className="w-5 h-5 shrink-0" aria-hidden />
          ) : (
            <HiChevronDown className="w-5 h-5 shrink-0" aria-hidden />
          )}
        </button>
        {helpOpen && (
          <div className="px-4 pb-4 border-t border-amber-200 pt-3">
            <p className="font-medium mb-1">Reply from here (recommended)</p>
            <p className="text-amber-900 mb-2">
              Expand a message, type your reply, and tap <strong>Send reply</strong>. The customer gets an
              email and the message is marked <strong>Replied</strong> automatically.
            </p>
            <p className="text-amber-900 mb-2">
              <strong>New</strong> / <strong>Read</strong> beside a name is the current status. The{' '}
              <strong>Mark as replied</strong> button on each row is an action — tap it after you have
              called or emailed the customer yourself.
            </p>
            <p className="font-medium mb-1">✓ Or from your notification email</p>
            <p className="text-amber-900 mb-2">
              New message emails include a green <strong>Mark as Replied</strong> button — no login needed
              after you call or email the customer yourself.
            </p>
            <p className="font-medium mb-1">💳 Quote or pay link</p>
            <p className="text-amber-900">
              When someone needs a price or wants to pay online, expand their message and tap{' '}
              <strong>Create pay link</strong>. Add menu items (or a custom line), optional pickup date,
              and they get a secure <strong>Pay now</strong> email — same as <strong>Send reply</strong>.
            </p>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-purple-600 mb-1">
            <HiInbox className="w-5 h-5" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-sm text-gray-600">Total</p>
        </div>
        <div className="bg-amber-50 rounded-xl border border-amber-100 p-4">
          <p className="text-2xl font-bold text-amber-800">{stats.new}</p>
          <p className="text-sm text-gray-600">New</p>
        </div>
        <div className="bg-blue-50 rounded-xl border border-blue-100 p-4">
          <p className="text-2xl font-bold text-blue-800">{stats.read}</p>
          <p className="text-sm text-gray-600">Read</p>
        </div>
        <div className="bg-green-50 rounded-xl border border-green-100 p-4">
          <p className="text-2xl font-bold text-green-800">{stats.replied}</p>
          <p className="text-sm text-gray-600">Replied</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-700">Show:</span>
        {(['all', 'new', 'read', 'replied', 'spam'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === s
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {s === 'all'
              ? 'All'
              : s === 'spam'
                ? `Spam${stats.spam > 0 ? ` (${stats.spam})` : ''}`
                : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:ml-auto">
          <input
            type="search"
            placeholder="Filter by subject…"
            value={subjectContains}
            onChange={(e) => setSubjectContains(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setSubjectApply(subjectContains);
            }}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 min-w-[160px] flex-1 sm:flex-none"
          />
          <button
            type="button"
            onClick={() => setSubjectApply(subjectContains)}
            className="px-3 py-1.5 text-sm font-medium bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200"
          >
            Apply subject
          </button>
          <button
            onClick={() => {
              setLoading(true);
              Promise.all([fetchSubmissions(), fetchStats()]).finally(() => setLoading(false));
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-purple-600 hover:bg-purple-50 rounded-lg"
          >
            <HiRefresh className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Batch actions */}
      {(stats.new > 0 || stats.read > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          {stats.new > 0 && (
            <button
              type="button"
              onClick={() => bulkUpdateStatus('read')}
              disabled={bulkUpdating}
              className="px-3 py-1.5 text-sm font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 rounded-lg disabled:opacity-50"
            >
              {bulkUpdating ? 'Updating…' : `Mark all ${stats.new} new as read`}
            </button>
          )}
          {stats.new + stats.read > 0 && (
            <button
              type="button"
              onClick={() => bulkUpdateStatus('replied')}
              disabled={bulkUpdating}
              className="px-3 py-1.5 text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200 rounded-lg disabled:opacity-50"
            >
              {bulkUpdating ? 'Updating…' : 'Mark all as replied'}
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-12">
          <HiRefresh className="w-8 h-8 text-purple-500 animate-spin" />
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <HiMail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No messages yet</p>
          <p className="text-sm text-gray-500 mt-1">
            When someone uses your contact form, messages will appear here and you&apos;ll get an
            email.
          </p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-2">
          {filtered.map((s) => {
            const isExpanded = expandedId === s.id;
            return (
              <div
                key={s.id}
                className={`bg-white rounded-xl border overflow-hidden ${
                  s.status === 'new' ? 'border-amber-200' : 'border-gray-200'
                }`}
              >
                <div className="flex items-stretch gap-2 p-2 sm:p-3">
                  <button
                    type="button"
                    onClick={() => toggleExpanded(s)}
                    className="flex-1 flex items-center gap-3 p-2 text-left hover:bg-gray-50 rounded-lg transition-colors min-w-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">{s.name}</span>
                        {s.status === 'new' && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded">
                            New
                          </span>
                        )}
                        {s.status === 'read' && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                            Read
                          </span>
                        )}
                        {s.status === 'replied' && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">
                            Replied
                          </span>
                        )}
                        {s.status === 'spam' && (
                          <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs font-medium rounded">
                            Spam
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate mt-0.5">
                        {s.subject || 'General inquiry'} · {formatDate(s.created_at)}
                      </p>
                    </div>
                    {isExpanded ? (
                      <HiChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    ) : (
                      <HiChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {s.status !== 'replied' && s.status !== 'spam' && (
                    <button
                      type="button"
                      disabled={updatingId === s.id}
                      onClick={() => void updateStatus(s.id, 'replied')}
                      className="shrink-0 self-center inline-flex items-center gap-1.5 px-3 py-2.5 min-h-[44px] border-2 border-green-600 bg-white text-green-800 rounded-lg text-sm font-semibold hover:bg-green-50 disabled:opacity-50 shadow-sm"
                      title="Tap when you have replied to this customer (by phone, email, or portal)"
                    >
                      <HiCheck className="w-4 h-4" aria-hidden />
                      <span className="hidden sm:inline">Mark as replied</span>
                      <span className="sm:hidden">Mark replied</span>
                    </button>
                  )}
                  {s.phone && s.status !== 'replied' && s.status !== 'spam' && (
                    <a
                      href={`tel:${s.phone.replace(/\D/g, '')}`}
                      className="shrink-0 self-center px-3 py-3 min-h-[44px] flex items-center bg-purple-100 text-purple-900 rounded-xl text-sm font-semibold hover:bg-purple-200"
                      title="Call customer"
                    >
                      <HiPhone className="w-5 h-5" />
                    </a>
                  )}
                </div>
                {isExpanded && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50/50 space-y-4">
                    <div className="grid gap-2 text-sm">
                      <p className="flex items-center gap-2">
                        <HiUser className="w-4 h-4 text-gray-400" />
                        <span>{s.name}</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <HiMail className="w-4 h-4 text-gray-400" />
                        <a
                          href={`mailto:${s.email}`}
                          className="text-purple-600 hover:underline"
                        >
                          {s.email}
                        </a>
                      </p>
                      {s.phone && (
                        <p className="flex items-center gap-2">
                          <HiPhone className="w-4 h-4 text-gray-400" />
                          <a href={`tel:${s.phone}`} className="text-purple-600 hover:underline">
                            {s.phone}
                          </a>
                        </p>
                      )}
                      <p className="font-medium text-gray-700 mt-2">Message:</p>
                      <p className="whitespace-pre-wrap text-gray-600 bg-white p-3 rounded-lg border border-gray-100">
                        {s.message}
                      </p>
                    </div>
                    {(s.replies?.length ?? 0) > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-700">Your replies (sent from portal)</p>
                        {s.replies!.map((r) => (
                          <div
                            key={r.id}
                            className="bg-violet-50 border border-violet-100 rounded-lg p-3 text-sm text-gray-700"
                          >
                            <p className="text-xs text-violet-700 mb-1">{formatDate(r.created_at)}</p>
                            <p className="whitespace-pre-wrap">{r.body}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reply to customer
                      </label>
                      <textarea
                        value={replyDraftById[s.id] ?? ''}
                        onChange={(e) =>
                          setReplyDraftById((d) => ({
                            ...d,
                            [s.id]: e.target.value,
                          }))
                        }
                        rows={4}
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
                        placeholder="Type your reply — the customer will receive this by email…"
                      />
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <button
                          type="button"
                          disabled={sendingReplyId === s.id || detailLoadingId === s.id}
                          onClick={() => void sendPortalReply(s)}
                          className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 disabled:opacity-50"
                        >
                          <HiPaperAirplane className="w-4 h-4" />
                          {sendingReplyId === s.id ? 'Sending…' : 'Send reply'}
                        </button>
                        <a
                          href={`mailto:${s.email}?subject=${encodeURIComponent(`Re: ${s.subject || 'General Inquiry'}`)}`}
                          className="text-sm text-purple-600 hover:underline"
                        >
                          Or use my email app
                        </a>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Admin notes (only you)</label>
                      <textarea
                        value={notesDraftById[s.id] ?? s.admin_notes ?? ''}
                        onChange={(e) =>
                          setNotesDraftById((d) => ({
                            ...d,
                            [s.id]: e.target.value,
                          }))
                        }
                        rows={2}
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
                        placeholder="Internal notes…"
                      />
                      <button
                        type="button"
                        disabled={updatingId === s.id}
                        onClick={() => void saveAdminNotes(s)}
                        className="mt-1 text-sm text-purple-600 hover:underline disabled:opacity-50"
                      >
                        Save notes
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-2">
                      {s.status === 'spam' ? (
                        <button
                          type="button"
                          disabled={updatingId === s.id}
                          onClick={() => updateStatus(s.id, 'read')}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium hover:bg-blue-200 disabled:opacity-50"
                        >
                          Not spam — move to Read
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={updatingId === s.id}
                          onClick={() => updateStatus(s.id, 'spam')}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-300 disabled:opacity-50"
                        >
                          Mark as spam
                        </button>
                      )}
                      {s.status !== 'read' && s.status !== 'spam' && (
                        <button
                          type="button"
                          disabled={updatingId === s.id}
                          onClick={() => updateStatus(s.id, 'read')}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium hover:bg-blue-200 disabled:opacity-50"
                        >
                          <HiCheck className="w-4 h-4" />
                          Mark read
                        </button>
                      )}
                      {s.status !== 'replied' && s.status !== 'spam' && (
                        <button
                          type="button"
                          disabled={updatingId === s.id}
                          onClick={() => updateStatus(s.id, 'replied')}
                          className="flex items-center gap-1.5 px-3 py-1.5 border border-green-600 bg-white text-green-800 rounded-lg text-sm font-medium hover:bg-green-50 disabled:opacity-50"
                        >
                          <HiCheck className="w-4 h-4" aria-hidden />
                          Mark as replied
                        </button>
                      )}
                      {s.status !== 'spam' && (
                        <button
                          type="button"
                          onClick={() => setQuoteFor(s)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-100 text-violet-900 rounded-lg text-sm font-medium hover:bg-violet-200"
                        >
                          <HiCurrencyDollar className="w-4 h-4" />
                          Create pay link
                        </button>
                      )}
                      <Link
                        to="/admin/orders"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-200"
                      >
                        Open Orders
                      </Link>
                      <button
                        type="button"
                        onClick={() => deleteSubmission(s.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
                      >
                        <HiTrash className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {quoteFor && (
        <CreateQuotePayLinkModal
          apiUrl={API_URL}
          contactSubmissionId={quoteFor.id}
          contactName={quoteFor.name}
          onClose={() => setQuoteFor(null)}
          onCreated={(payUrl, orderNumber) => {
            void navigator.clipboard.writeText(payUrl).catch(() => {});
            window.alert(
              `Order ${orderNumber ? `#${orderNumber} ` : ''}created.\n\nCustomer pay link:\n${payUrl}\n\nLink copied to clipboard when your browser allows.`
            );
          }}
        />
      )}
    </div>
  );
}
